import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { 
  Heart, Users, Trophy, ArrowRight, Star, Wallet, QrCode, BarChart3, 
  Bell, Leaf, Shield, Smartphone, CreditCard, Mail, Target, 
  TrendingUp, Award, Globe, CheckCircle, Zap, Building2, 
  PieChart, MessageSquare, Settings
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import AuthRedirect from '@/components/auth/AuthRedirect'
import { useState, useEffect } from 'react'

const Index = () => {
  const { user, userRole, loading } = useAuth()
  const [registrationType, setRegistrationType] = useState<string>('customer')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  // Handle invitation redirect
  useEffect(() => {
    const inviteToken = searchParams.get('invite')
    if (inviteToken) {
      navigate(`/einladung/${inviteToken}`, { replace: true })
    }
  }, [searchParams, navigate])
  
  // If user is already logged in, redirect to appropriate dashboard
  if (!loading && user) {
    return <AuthRedirect />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-20">
        <div className="text-center max-w-5xl mx-auto">
          <div className="flex items-center justify-center mb-8">
            <div className="bg-gradient-to-r from-primary to-primary-glow p-4 rounded-full shadow-glow">
              <Heart className="h-12 w-12 text-primary-foreground" />
            </div>
          </div>
          
          <Badge variant="secondary" className="mb-6 px-4 py-2 text-sm font-medium">
            <Building2 className="h-4 w-4 mr-2" />
            Deutschlands führende digitale Treuekarten-Plattform
          </Badge>
          
          <h1 className="text-6xl font-bold mb-8 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            Herzensempfehlung.de
          </h1>
          
          <p className="text-2xl text-muted-foreground mb-6 max-w-3xl mx-auto leading-relaxed">
            Revolutionieren Sie Ihre Kundenbindung mit digitalen Treuekarten, intelligentem Empfehlungsmanagement und automatisierten Belohnungssystemen
          </p>
          
          
          {/* Registration Type Selection */}
          <div className="max-w-md mx-auto mb-8">
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-lg font-medium mb-3">Wie möchten Sie sich registrieren?</p>
                <Select value={registrationType} onValueChange={setRegistrationType}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Registrierungsart wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Als Kunde</SelectItem>
                    <SelectItem value="business">Als Unternehmen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              

              {/* Business Value Proposition */}
              {registrationType === 'business' && (
                <div className="bg-gradient-to-r from-primary/5 to-primary-glow/5 p-6 rounded-lg border border-primary/20">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-primary" />
                      <span>+40% Kundenbindung</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary" />
                      <span>+25% Neukunden</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <PieChart className="h-4 w-4 text-primary" />
                      <span>ROI bis 300%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-primary" />
                      <span>100% Automatisiert</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to={`/registrieren?type=${registrationType}`}>
              <Button size="lg" className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-glow transition-all duration-300 px-8 py-4">
                {registrationType === 'customer' ? 'Kostenlos als Kunde starten' : 'Kostenlos testen - 14 Tage'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            
            <Link to="/anmelden">
              <Button variant="outline" size="lg" className="px-8 py-4">
                Anmelden
              </Button>
            </Link>
          </div>
          
        </div>
      </div>
      
      {/* Core Features Section */}
      <div className="bg-card/50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Alles was Sie für erfolgreiche Kundenbindung brauchen</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Unsere All-in-One-Plattform kombiniert modernste Technologie mit benutzerfreundlicher Bedienung
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center shadow-soft border-0 bg-background/80 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Wallet className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Digitale Wallet-Karten</h3>
                <p className="text-sm text-muted-foreground">
                  Apple & Google Wallet Integration mit Live-Updates und Push-Benachrichtigungen
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center shadow-soft border-0 bg-background/80 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="bg-accent/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <QrCode className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2">QR-Code System</h3>
                <p className="text-sm text-muted-foreground">
                  Einfaches Scannen für Stempel-Vergabe und kontaktlose Kundenerfahrung
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center shadow-soft border-0 bg-background/80 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="bg-success/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Users className="h-8 w-8 text-success" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Empfehlungs-CRM</h3>
                <p className="text-sm text-muted-foreground">
                  Vollautomatisches Lead-Management mit Follow-Up-System
                </p>
              </CardContent>
            </Card>
            
            <Card className="text-center shadow-soft border-0 bg-background/80 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <BarChart3 className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Analytics Dashboard</h3>
                <p className="text-sm text-muted-foreground">
                  Detaillierte Insights und ROI-Tracking für datenbasierte Entscheidungen
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Advanced Features Section */}
      <div className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Erweiterte Features für maximalen Erfolg</h2>
            <p className="text-xl text-muted-foreground">
              Professionelle Tools für Unternehmen jeder Größe
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="bg-primary/10 p-3 rounded-full flex-shrink-0">
                  <Trophy className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Wettbewerbs-System</h3>
                  <p className="text-muted-foreground">Multi-Tenant-Wettbewerbe mit automatischer Teilnahme und fairer Gewinner-Ziehung für maximale Kundenaktivierung.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="bg-accent/10 p-3 rounded-full flex-shrink-0">
                  <Globe className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Landing Page Builder</h3>
                  <p className="text-muted-foreground">Drag & Drop Editor mit professionellen Templates für individuelle Kampagnen und Custom-Domain-Support.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="bg-success/10 p-3 rounded-full flex-shrink-0">
                  <Bell className="h-6 w-6 text-success" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Push-Benachrichtigungen</h3>
                  <p className="text-muted-foreground">Automatisierte Web-Push, E-Mail und Wallet-Updates für optimale Kundenkommunikation.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="bg-primary/10 p-3 rounded-full flex-shrink-0">
                  <Leaf className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Soziale Projekte</h3>
                  <p className="text-muted-foreground">Automatische Baum-Spenden basierend auf Kundenaktivität für nachhaltiges Corporate Social Responsibility.</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <Card className="p-6 text-center">
                <div className="bg-primary/10 p-3 rounded-full w-fit mx-auto mb-4">
                  <Target className="h-8 w-8 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Zielgruppen-Segmentierung</h4>
                <p className="text-sm text-muted-foreground">Personalisierte Kampagnen</p>
              </Card>
              
              <Card className="p-6 text-center">
                <div className="bg-accent/10 p-3 rounded-full w-fit mx-auto mb-4">
                  <Smartphone className="h-8 w-8 text-accent" />
                </div>
                <h4 className="font-semibold mb-2">PWA-Technologie</h4>
                <p className="text-sm text-muted-foreground">App-Erlebnis ohne Download</p>
              </Card>
              
              <Card className="p-6 text-center">
                <div className="bg-success/10 p-3 rounded-full w-fit mx-auto mb-4">
                  <MessageSquare className="h-8 w-8 text-success" />
                </div>
                <h4 className="font-semibold mb-2">Automatisierte E-Mails</h4>
                <p className="text-sm text-muted-foreground">Follow-Up-Sequenzen</p>
              </Card>
              
              <Card className="p-6 text-center">
                <div className="bg-primary/10 p-3 rounded-full w-fit mx-auto mb-4">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Gamification</h4>
                <p className="text-sm text-muted-foreground">Leaderboards & Badges</p>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* ROI & Benefits Section */}
      <div className="bg-gradient-to-r from-primary/5 to-primary-glow/5 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Messbare Ergebnisse für Ihr Unternehmen</h2>
            <p className="text-xl text-muted-foreground">
              Unsere Kunden verzeichnen durchschnittlich diese Verbesserungen
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">+40%</div>
              <div className="text-lg font-semibold mb-1">Kundenbindung</div>
              <div className="text-sm text-muted-foreground">Durch digitale Treuekarten</div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-accent mb-2">+25%</div>
              <div className="text-lg font-semibold mb-1">Neukunden</div>
              <div className="text-sm text-muted-foreground">Via Empfehlungsprogramm</div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-success mb-2">300%</div>
              <div className="text-lg font-semibold mb-1">ROI</div>
              <div className="text-sm text-muted-foreground">Return on Investment</div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">75%</div>
              <div className="text-lg font-semibold mb-1">Zeitersparnis</div>
              <div className="text-sm text-muted-foreground">Durch Automatisierung</div>
            </div>
          </div>
          
          <Card className="max-w-4xl mx-auto p-8 bg-background/80 backdrop-blur-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-success" />
                  Für Ihr Unternehmen
                </h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                    Erhöhte Kundenbindung und Wiederkaufsrate
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                    Kosteneffektive Neukundengewinnung
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                    Detaillierte Kundenanalysen und Insights
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                    Automatisierte Marketing-Prozesse
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-success" />
                  Für Ihre Kunden
                </h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                    Digitale Wallet-Integration ohne App-Download
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                    Transparente Punkteübersicht und Prämien
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                    Gamification durch Wettbewerbe
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                    Unterstützung sozialer Projekte
                  </li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </div>


      {/* Security & Trust Section */}
      <div className="bg-card/50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Sicherheit & Vertrauen</h2>
            <p className="text-xl text-muted-foreground">
              Enterprise-Grade Sicherheit für Ihre Kundendaten
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <Card className="p-6 text-center">
              <div className="bg-success/10 p-4 rounded-full w-fit mx-auto mb-4">
                <Shield className="h-8 w-8 text-success" />
              </div>
              <h3 className="text-lg font-semibold mb-2">DSGVO-Konform</h3>
              <p className="text-sm text-muted-foreground">
                Vollständige Compliance mit deutschen und europäischen Datenschutzbestimmungen
              </p>
            </Card>
            
            <Card className="p-6 text-center">
              <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                <CreditCard className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Sichere Zahlungen</h3>
              <p className="text-sm text-muted-foreground">
                Stripe-Integration für PCI-DSS konforme und sichere Zahlungsabwicklung
              </p>
            </Card>
            
            <Card className="p-6 text-center">
              <div className="bg-accent/10 p-4 rounded-full w-fit mx-auto mb-4">
                <Settings className="h-8 w-8 text-accent" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Audit-Logging</h3>
              <p className="text-sm text-muted-foreground">
                Umfassendes Logging aller Aktionen für Compliance und Sicherheitsmonitoring
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="py-20 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Bereit für mehr Kundenbindung?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Starten Sie noch heute und entdecken Sie, wie einfach digitale Kundenbindung sein kann
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/registrieren?type=business">
              <Button size="lg" variant="secondary" className="px-8 py-4 text-primary bg-background hover:bg-background/90">
                Jetzt kostenlos testen
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            
            <Link to="/anmelden">
              <Button size="lg" variant="outline" className="px-8 py-4 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                Demo buchen
              </Button>
            </Link>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default Index;
